import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { 
  FaCogs, 
  FaStore, 
  FaDesktop, 
  FaUsers, 
  FaChartBar, 
  FaPlug,
  FaPaw
} from 'react-icons/fa';
import type { Product } from '../types';
import { AnimationVariants, StyledComponentProps } from '../types';
import { useLanguage } from '../i18n';

const ProductsContainer = styled.section`
  padding: 100px 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const ProductCard = styled(motion.div)<StyledComponentProps>`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.borderLight};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || props.theme.gradients.primary};
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows.xl};
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const ProductIcon = styled.div<StyledComponentProps>`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${props => props.gradient || props.theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  margin-bottom: 24px;
`;

const ProductTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.textDark};
  margin-bottom: 16px;
`;

const ProductDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const ProductImageContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  /* Hide scrollbar for cleaner look but allow scrolling */
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0,0,0,0.1);
    border-radius: 4px;
  }

  img {
    height: 180px;
    width: auto;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0,0,0,0.05);
    flex-shrink: 0;
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
`;

const ProductFeatures = styled.div`
  margin-bottom: 32px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: ${props => props.theme.colors.textLight};
  
  .feature-icon {
    width: 20px;
    height: 20px;
    background: ${props => props.theme.colors.success};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: white;
  }
`;

const ProductButton = styled(motion.button)<StyledComponentProps>`
  background: ${props => props.gradient || props.theme.gradients.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

interface ProductsProps {
  id?: string;
}

const Products: React.FC<ProductsProps> = ({ id }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const products: Product[] = [
    {
      icon: <FaCogs />,
      title: t('product.medical.title'),
      description: t('product.medical.desc'),
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      features: [
        t('product.medical.f1'),
        t('product.medical.f2'),
        t('product.medical.f3'),
        t('product.medical.f4')
      ]
    },
    {
      icon: <FaStore />,
      title: t('product.legal.title'),
      description: t('product.legal.desc'),
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      features: [
        t('product.legal.f1'),
        t('product.legal.f2'),
        t('product.legal.f3'),
        t('product.legal.f4')
      ]
    },
    {
      icon: <FaDesktop />,
      title: t('product.hr.title'),
      description: t('product.hr.desc'),
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      features: [
        t('product.hr.f1'),
        t('product.hr.f2'),
        t('product.hr.f3'),
        t('product.hr.f4')
      ]
    },
    {
      icon: <FaChartBar />,
      title: t('product.consult.title'),
      description: t('product.consult.desc'),
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      features: [
        t('product.consult.f1'),
        t('product.consult.f2'),
        t('product.consult.f3'),
        t('product.consult.f4')
      ]
    },
    {
      icon: <FaUsers />,
      title: t('product.custom.title'),
      description: t('product.custom.desc'),
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      features: [
        t('product.custom.f1'),
        t('product.custom.f2'),
        t('product.custom.f3'),
        t('product.custom.f4')
      ]
    },
    {
      icon: <FaPlug />,
      title: t('product.transform.title'),
      description: t('product.transform.desc'),
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      features: [
        t('product.transform.f1'),
        t('product.transform.f2'),
        t('product.transform.f3'),
        t('product.transform.f4')
      ]
    },
    {
      icon: <FaPaw />,
      title: t('product.petmoji.title'),
      description: t('product.petmoji.desc'),
      gradient: "linear-gradient(135deg, #FF9A9E 0%, #FECFEF 99%, #FECFEF 100%)",
      features: [
        t('product.petmoji.f1'),
        t('product.petmoji.f2'),
        t('product.petmoji.f3'),
        t('product.petmoji.f4')
      ],
      images: [
        "/images/products/petmoji/screenshot_1.png",
        "/images/products/petmoji/screenshot_2.png"
      ]
    }
  ];

  const containerVariants: AnimationVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants: AnimationVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <ProductsContainer id={id}>
      <Container>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            {t('products.section.title')}
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {t('products.section.subtitle')}
          </SectionSubtitle>
        </SectionHeader>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <ProductsGrid>
            {products.map((product, index) => (
              <ProductCard
                key={index}
                variants={itemVariants}
                gradient={product.gradient}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ProductIcon gradient={product.gradient}>
                  {product.icon}
                </ProductIcon>
                <ProductTitle>{product.title}</ProductTitle>
                <ProductDescription>{product.description}</ProductDescription>
                
                {product.images && product.images.length > 0 && (
                  <ProductImageContainer>
                    {product.images.map((img, idx) => (
                      <img key={idx} src={img} alt={`${product.title} screenshot ${idx + 1}`} />
                    ))}
                  </ProductImageContainer>
                )}

                <ProductFeatures>
                  {product.features.map((feature, featureIndex) => (
                    <FeatureItem key={featureIndex}>
                      <div className="feature-icon">✓</div>
                      <span>{feature}</span>
                    </FeatureItem>
                  ))}
                </ProductFeatures>
                <ProductButton
                  gradient={product.gradient}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('products.more')}
                </ProductButton>
              </ProductCard>
            ))}
          </ProductsGrid>
        </motion.div>
      </Container>
    </ProductsContainer>
  );
};

export default Products;
